import { Fragment, useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Avatar from '@mui/material/Avatar'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import UsersIcon from '@mui/icons-material/People'
import { alpha, useTheme } from '@mui/material/styles'
import api from 'services/api'
import { Stamp, useClockText } from 'components/core/Clock'
import LedgerTable, { OutcomeChip, routerHeardPath } from './LedgerTable'
import SpanCost from './SpanCost'
import WholeSpan, {
  spanWordsLine,
  useLedgerPlay,
  useMomentSpan,
} from './WholeSpan'
import { MOMENT_OUTCOMES, litSx } from './vocabulary'
import { fmtSeconds } from '../StreamIngest/vocabulary'

// One moment's card (step 4a). A card is what the phone will show — the quote
// in the product's own marks, the hook under it — plus what only staff see:
// how the duel went (wins out of opponents faced, the threshold), the model's
// strength, who said it, and the media verdict — flagged, and withheld from
// the phone when the rule's mode is withhold (`mediaMode`). Open reports about
// the quote (step 6b) are a red chip that opens the Reports page. The card's
// play button hears the quote's own window, as the phone will, the line lit
// word by word. The account's name narrows the page (and the Fleet box) to it;
// the icon beside it opens their profile on the Users page, where staff reach
// them.
//
// The chevron under the play button, or "Whole moment", opens the moment:
// its transcript with a play/pause of its own for the whole cut, the words lit
// as they are said (click any word, or a speaker, to hear it from there — the
// same jump the span transcripts on Stream Ingest have); then what the span
// cost and how long it took; then every moment the selector found in the span
// with this card's row marked, so what the judge turned down beside it reads
// in place, each row playable and timed (that order: the owner's,
// 2026-10-05); and last the whole span it was cut from, every word, the moment
// marked (`WholeSpan`, owner 2026-11-04). The span's ledger and its words are
// fetched once, on first open.
//
// A span's ledger row on the spans table renders it; the page's two-pane list
// (`MomentsSplit`) is built from its parts — the line, the chips, the whole
// moment, the span's spend and tally. One audio element, `player`, plays for
// all of them.

// The play button's and the chevron's tooltips wait this long, so they never
// stand in the way of a cursor moving between the two.
const TOOLTIP_DELAY_MS = 1000

const LABEL_TEXT = {
  owner: 'the account holder',
  other: 'another voice',
  unattributed: 'unattributed',
  media: 'media',
}

// The quote's own words: those of the moment's turns that start inside the
// quote's window (client-feed.md's rule), each with its timing. Empty when the
// window is unknown, and the card then shows the stored text unlit.
const quoteWords = (moment) => {
  const quote = moment.quote || {}
  if (quote.start == null || quote.end == null) return []
  return (moment.turns || []).flatMap((turn) =>
    turn.words.filter(
      ([, start]) => start >= quote.start - 0.001 && start <= quote.end
    )
  )
}

// Which word is being said: the last one whose start the playhead has passed,
// so the light holds across the gap between two words rather than flickering
// off. `null` before the first word or with nothing playing.
const litIndex = (words, active, time) => {
  if (!active) return null
  let lit = null
  words.forEach(([, start], index) => {
    if (time >= start - 0.02) lit = index
  })
  return lit
}

// The card's line, lit as it plays: words already said at full ink, the word
// being said on the pill, the words still to come dimmed (reclip-08's
// `unsaidInk`, 0.28). Unlit — every word at full ink — whenever this moment is
// not the one playing. The two-pane list sets it smaller (`variant`).
export const QuoteLine = ({ moment, active, time, variant = 'h6' }) => {
  const theme = useTheme()
  const words = useMemo(() => quoteWords(moment), [moment])
  const lit = litIndex(words, active, time)
  const tokens = words.length ? words : null
  return (
    <Typography variant={variant} fontWeight={700} sx={{ lineHeight: 1.45 }}>
      “
      {tokens
        ? tokens.map(([text], index) => {
            const said = lit != null && index < lit
            const unsaid = active && (lit == null || index > lit)
            return (
              <Fragment key={`${text}-${index}`}>
                <Box
                  component="span"
                  sx={{
                    opacity: unsaid && !said ? 0.28 : 1,
                    transition: 'opacity 120ms linear',
                    ...(index === lit ? litSx(theme) : {}),
                  }}
                >
                  {text}
                </Box>
                {index < tokens.length - 1 ? ' ' : ''}
              </Fragment>
            )
          })
        : moment.quote?.text}
      ”
    </Typography>
  )
}

const Transcript = ({ moment, playing, time, onSeek }) => {
  const theme = useTheme()
  const quote = moment.quote || {}
  const canPlay = Boolean(moment.audio)
  const quoteTint = alpha(theme.palette.success.main, 0.14)
  const within = (start) =>
    quote.start != null && start >= quote.start - 0.001 && start <= quote.end
  return (
    <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {(moment.turns || []).map((turn, index) => (
        <Box
          key={`${turn.speaker}-${turn.start}-${index}`}
          sx={{ display: 'flex', gap: 1 }}
        >
          <Tooltip title={canPlay ? 'Hear this turn' : ''} placement="left">
            <Typography
              variant="caption"
              onClick={canPlay ? () => onSeek(turn.start) : undefined}
              sx={{
                minWidth: 92,
                color: turn.is_owner ? 'primary.main' : 'text.secondary',
                pt: 0.25,
                cursor: canPlay ? 'pointer' : 'default',
                '&:hover': canPlay ? { textDecoration: 'underline' } : {},
              }}
            >
              {turn.is_owner ? 'you' : turn.speaker}
            </Typography>
          </Tooltip>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            {turn.words.map(([text, start, end], position) => {
              const inQuote = within(start)
              const nextInQuote =
                position < turn.words.length - 1 &&
                within(turn.words[position + 1][1])
              const spoken = playing && time >= start && time < end + 0.05
              return (
                <Fragment key={`${start}-${position}`}>
                  <Box
                    component="span"
                    onClick={canPlay ? () => onSeek(start) : undefined}
                    sx={{
                      backgroundColor: inQuote ? quoteTint : 'transparent',
                      borderRadius: 0.5,
                      cursor: canPlay ? 'pointer' : 'default',
                      // Hover tints a word you could click; the word being
                      // said keeps its pill, or a click would hide it.
                      ...(canPlay && !spoken
                        ? {
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.12
                              ),
                            },
                          }
                        : {}),
                      ...(spoken ? litSx(theme) : {}),
                    }}
                  >
                    {text}
                  </Box>
                  {position < turn.words.length - 1 && (
                    // The space between two words, outside both: no pill or
                    // hover reaches it, and the quote's band crosses it only
                    // when the words on both sides are in the quote.
                    <Box
                      component="span"
                      sx={{
                        backgroundColor:
                          inQuote && nextInQuote ? quoteTint : 'transparent',
                      }}
                    >
                      {' '}
                    </Box>
                  )}
                </Fragment>
              )
            })}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

// The quote's play button: its own window, as the phone plays it. Its tooltip
// is slow to appear, above, and gone once the cursor leaves: below and quick,
// it covered the card's chevron the cursor was heading for.
export const QuoteButton = ({ moment, player }) => {
  const quotePlaying =
    player.current?.id === moment.id && !player.current.whole && !player.paused
  return (
    <Tooltip
      placement="top"
      disableInteractive
      enterDelay={TOOLTIP_DELAY_MS}
      enterNextDelay={TOOLTIP_DELAY_MS}
      title={
        moment.audio
          ? 'Play the quote, as the phone will'
          : 'The cut failed: no audio'
      }
    >
      <span>
        <IconButton
          size="small"
          disabled={!moment.audio}
          onClick={() => player.playQuote(moment)}
        >
          {player.loading === moment.id ? (
            <CircularProgress size={18} />
          ) : quotePlaying ? (
            <PauseIcon />
          ) : (
            <PlayArrowIcon />
          )}
        </IconButton>
      </span>
    </Tooltip>
  )
}

// The whole moment: a play/pause of its own for the whole cut, beside its
// transcript.
export const WholeMoment = ({ moment, player }) => {
  const playing = player.current?.id === moment.id
  const wholePlaying = playing && player.current.whole && !player.paused
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Tooltip
        title={
          moment.audio
            ? wholePlaying
              ? 'Pause the whole moment'
              : 'Play the whole moment'
            : 'The cut failed: no audio'
        }
      >
        <span>
          <IconButton
            size="small"
            color="primary"
            disabled={!moment.audio}
            onClick={() => player.playWhole(moment)}
          >
            {wholePlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </span>
      </Tooltip>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Transcript
          moment={moment}
          playing={playing}
          time={player.time}
          onSeek={(seconds) => player.seek(moment, seconds)}
        />
      </Box>
    </Box>
  )
}

// The span this card came from, read once the card is first opened (`seen`
// only ever turns on): its ledger carries what the span cost, how long it
// took, and every moment the selector found there.
export const useSpanLedger = (moment, seen) => {
  const [ledger, setLedger] = useState(null)
  const [error, setError] = useState(null)
  const known = moment.span_start_ms != null
  useEffect(() => {
    if (!seen || !known) return undefined
    let live = true
    api.streamMetrics
      .getSelectionSpan({
        userId: moment.user_id,
        spanStartMs: moment.span_start_ms,
      })
      .then((data) => live && setLedger(data))
      .catch((err) => live && setError(err.message))
    return () => {
      live = false
    }
  }, [seen, known, moment.user_id, moment.span_start_ms])
  return { known, ledger, error }
}

// After the transcript, before the span's moments: what the span cost and
// how long it took.
const SpanSpend = ({ moment, span }) => {
  if (!span.known) return null
  if (span.error) {
    return (
      <Alert severity="info" sx={{ mt: 1.5 }}>
        {span.error === 'no such span'
          ? 'The span’s ledger is gone: no row (90 days) and no trace.'
          : span.error}
      </Alert>
    )
  }
  if (!span.ledger) return <CircularProgress size={18} sx={{ mt: 1.5 }} />
  if (!span.ledger.job) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        No selection job on the span.
      </Typography>
    )
  }
  return (
    <Box sx={{ mt: 1 }}>
      <SpanCost ledger={span.ledger} moment={moment} />
    </Box>
  )
}

// The span's line: when it started, how long it ran, how many moments the
// selector found there and what became of them.
export const SpanTally = ({ moment, ledger }) => {
  const rows = ledger?.job?.moments || []
  const tally = rows.reduce((counts, row) => {
    const key = row.outcome || 'owed'
    return { ...counts, [key]: (counts[key] || 0) + 1 }
  }, {})
  return (
    <>
      <Typography variant="caption" color="text.secondary">
        <Stamp ms={moment.span_start_ms} zone={moment.timezone} />
        {ledger.span_end_ms
          ? ` · ${Math.round(
              (ledger.span_end_ms - ledger.span_start_ms) / 60000
            )} min`
          : ''}
        {` · ${rows.length} found`}
      </Typography>
      {MOMENT_OUTCOMES.filter((o) => tally[o.key]).map((o) => (
        <OutcomeChip key={o.key} outcome={o.key} count={tally[o.key]} />
      ))}
    </>
  )
}

// Only where the server says the caller may hear the span: their own, or
// anyone's on develop (STREAM_ADMIN_PLAYBACK).
export const RouterHeardButton = ({ moment, ledger, sx }) =>
  ledger.playable ? (
    <Button
      size="small"
      component={Link}
      to={routerHeardPath({
        userId: moment.user_id,
        username: moment.username,
        spanStartMs: moment.span_start_ms,
      })}
      sx={{ textTransform: 'none', ...sx }}
    >
      Open in What the router heard
    </Button>
  ) : null

// Every moment the selector found in the span, this card's row marked: what
// the judge turned down beside it, each playable (`useLedgerPlay`).
export const SpanLedger = ({ moment, rows, player, spanWords }) => {
  const plays = useLedgerPlay(moment, player, spanWords, rows)
  return (
    <>
      <LedgerTable
        rows={rows}
        markId={moment.id}
        zone={moment.timezone}
        onPlay={plays.onPlay}
        rowPlay={plays.rowPlay}
      />
      {plays.error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {plays.error}
        </Alert>
      )}
    </>
  )
}

const SpanMoments = ({ moment, ledger, player, spanWords }) => {
  const rows = ledger?.job?.moments || []
  if (!rows.length) return null
  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.75,
          mb: 0.5,
        }}
      >
        <Typography variant="subtitle2">From this span</Typography>
        <SpanTally moment={moment} ledger={ledger} />
        <RouterHeardButton
          moment={moment}
          ledger={ledger}
          sx={{ ml: 'auto' }}
        />
      </Box>
      <SpanLedger
        moment={moment}
        rows={rows}
        player={player}
        spanWords={spanWords}
      />
    </Box>
  )
}

// The moment at a glance: the duel, the model's strength, who said it, the
// media verdict, where its review stands, and what it carries (no audio,
// comments, open reports). The caller lays them out.
export const MomentChips = ({ moment, mediaMode }) => {
  const clockText = useClockText()
  const admission = moment.admission || {}
  const media = moment.media || {}
  const admitted =
    admission.bar_wins != null && admission.bar_wins >= admission.bar_threshold
  return (
    <>
      <Tooltip
        title={`Wins against the ${admission.bar_n} opponents faced; ${admission.bar_threshold} to ship. A refused card's count is a floor, an admitted one's exact.`}
      >
        <Chip
          size="small"
          color={admitted ? 'success' : 'default'}
          label={`bar ${admission.bar_wins ?? '—'}/${admission.bar_n ?? '—'}`}
        />
      </Tooltip>
      <Tooltip title="The model's own 1–5 rating; a prefilter at 3, never the bar.">
        <Chip
          size="small"
          variant="outlined"
          label={`strength ${admission.strength}`}
        />
      </Tooltip>
      <Tooltip title="Who said the quote: the turn's owner verdict, per transcription run. Abstention is a first-class answer.">
        <Chip
          size="small"
          variant="outlined"
          label={LABEL_TEXT[moment.speaker_label] || moment.speaker_label}
        />
      </Tooltip>
      {moment.media_would_withhold && (
        <Tooltip
          title={`Third-party media: score ${media.score ?? 'not scored'} (${
            media.features_version
          }). ${
            mediaMode === 'withhold'
              ? 'Withheld: the phone never receives it while the mode is withhold.'
              : `Mode ${
                  mediaMode || 'shadow'
                }: shown on the phone, withheld only when the mode is withhold.`
          }`}
        >
          <Chip
            size="small"
            color="warning"
            label={
              mediaMode === 'withhold' ? 'media · withheld' : 'media-flagged'
            }
          />
        </Tooltip>
      )}
      {!moment.media_would_withhold && moment.media_router_only && (
        <Tooltip
          title={`The router heard music or TV over the quote's segments, and the score ${
            media.score ?? 'is missing'
          } is under the cutoff. In shadow: the phone still receives it; the flags withhold only when STREAM_MEDIA_ROUTER_FLAGS is on.`}
        >
          <Chip
            size="small"
            variant="outlined"
            color="warning"
            label="router-flagged · shadow"
          />
        </Tooltip>
      )}
      {!moment.disabled_at_ms && moment.released_at_ms == null && (
        <Tooltip title="Waiting in its owner's tray for their review (step 5). Not in their feed until a press of “share” keeps it.">
          <Chip size="small" color="info" label="awaiting review" />
        </Tooltip>
      )}
      {!moment.disabled_at_ms && moment.released_at_ms != null && (
        <Tooltip
          title={`Kept by its owner's review ${clockText(
            moment.released_at_ms,
            moment.timezone
          )}, ${fmtSeconds(
            (moment.released_at_ms - moment.surfaced_at_ms) / 1000
          )} after it surfaced. In their feed.`}
        >
          <Chip size="small" color="success" variant="outlined" label="kept" />
        </Tooltip>
      )}
      {moment.disabled_at_ms && (
        <Chip
          size="small"
          color="default"
          label={`${
            moment.released_at_ms == null
              ? 'deleted in review'
              : 'hidden from feed'
          } · ${moment.disabled_reason || 'reason masked'}`}
        />
      )}
      {!moment.audio && <Chip size="small" color="error" label="no audio" />}
      {moment.comment_count > 0 && (
        <Tooltip title="Live comments in this quote's thread (step 6c), every author's: a count of rows that exist, tombstones not counted.">
          <Chip
            size="small"
            variant="outlined"
            label={`${moment.comment_count} comment${
              moment.comment_count === 1 ? '' : 's'
            }`}
          />
        </Tooltip>
      )}
      {moment.open_reports > 0 && (
        <Tooltip title="Open reports about this quote from its owner's friends (step 6b). The Reports page lists them with the snapshot, the reporter and the actions.">
          <Chip
            size="small"
            color="error"
            clickable
            component={Link}
            to="/reports"
            label={`${moment.open_reports} open report${
              moment.open_reports === 1 ? '' : 's'
            }`}
          />
        </Tooltip>
      )}
    </>
  )
}

// A person's profile picture, the Users list's, small, opening their profile
// on the Users page in a new tab so this page keeps its place. The people icon
// stands in for an account with no picture (most guests).
export const ProfileLink = ({ userId, picture, size = 18, sx }) => (
  <Tooltip placement="top" title="Profile">
    <IconButton
      size="small"
      aria-label="profile"
      component={Link}
      to={`/users?user=${userId}`}
      target="_blank"
      rel="noopener noreferrer"
      data-row-action
      sx={{ p: 0.25, verticalAlign: 'middle', ...sx }}
    >
      <Avatar
        src={picture || undefined}
        sx={{ width: size, height: size, bgcolor: 'action.selected' }}
      >
        <UsersIcon sx={{ fontSize: Math.round(size * 0.7) }} />
      </Avatar>
    </IconButton>
  </Tooltip>
)

// When it was recorded, on the page's clock (a click switches it between the
// staff member's and the account's, `components/core/Clock`), how long the cut
// is, and whose it is: the name narrows the page to the account, their picture
// beside it opens their profile. The time stays in this line, under the quote:
// the quote is the card's star (owner, 2026-11-04).
export const MomentMeta = ({ moment, onUser }) => (
  <Typography variant="caption" color="text.secondary">
    recorded <Stamp ms={moment.range_start_ms} zone={moment.timezone} /> ·{' '}
    {moment.duration ? `${moment.duration.toFixed(1)} s cut` : 'no cut'} ·{' '}
    <Tooltip title={`Only this account · ${moment.user_id}`}>
      <Box
        component="span"
        data-row-action
        onClick={() =>
          onUser({ id: moment.user_id, username: moment.username })
        }
        sx={{
          cursor: 'pointer',
          textDecoration: 'underline dotted',
        }}
      >
        {moment.username || moment.user_id.slice(-6)}
      </Box>
    </Tooltip>
    <ProfileLink
      userId={moment.user_id}
      picture={moment.picture}
      sx={{ ml: 0.25 }}
    />
  </Typography>
)

const MomentCard = ({ moment, player, onTrace, onUser, mediaMode }) => {
  const [open, setOpen] = useState(false)
  // The span's ledger, the trace read behind it included, is fetched on the
  // first open and kept.
  const [seen, setSeen] = useState(false)
  const span = useSpanLedger(moment, seen)
  const spanWords = useMomentSpan(moment, seen)
  const toggle = () => {
    setOpen((o) => !o)
    setSeen(true)
  }
  const playing = player.current?.id === moment.id
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.25 }}>
          <QuoteButton moment={moment} player={player} />
          <Tooltip
            title={open ? 'Hide the moment' : 'The whole moment'}
            disableInteractive
            enterDelay={TOOLTIP_DELAY_MS}
            enterNextDelay={TOOLTIP_DELAY_MS}
          >
            <IconButton size="small" onClick={toggle}>
              <ChevronRightIcon
                sx={{
                  transform: open ? 'rotate(90deg)' : 'none',
                  transition: 'transform 150ms',
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <QuoteLine
            moment={moment}
            active={playing && !player.paused}
            time={player.time}
          />
          <Typography variant="body2" color="text.secondary">
            {moment.quote?.hook}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.75,
              mt: 1,
              alignItems: 'center',
            }}
          >
            <MomentChips moment={moment} mediaMode={mediaMode} />
            <MomentMeta moment={moment} onUser={onUser} />
          </Box>
          {player.error?.id === moment.id && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {player.error.message}
            </Alert>
          )}
          {open && (
            <Box sx={{ mt: 1.5 }}>
              <WholeMoment moment={moment} player={player} />
            </Box>
          )}
          {open && <SpanSpend moment={moment} span={span} />}
          {open && (
            <SpanMoments
              moment={moment}
              ledger={span.ledger}
              player={player}
              spanWords={spanWords}
            />
          )}
          {open && spanWords.known && (
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  flexWrap: 'wrap',
                  gap: 0.75,
                  mb: 0.5,
                }}
              >
                <Typography variant="subtitle2">Whole span</Typography>
                <Typography variant="caption" color="text.secondary">
                  {spanWordsLine(moment, spanWords)}
                </Typography>
              </Box>
              <WholeSpan moment={moment} player={player} state={spanWords} />
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            alignItems: 'flex-end',
          }}
        >
          <Button size="small" onClick={toggle} sx={{ textTransform: 'none' }}>
            {open ? 'Hide moment' : 'Whole moment'}
          </Button>
          <Button
            size="small"
            disabled={!moment.trace}
            onClick={() => onTrace(moment.id)}
            sx={{ textTransform: 'none' }}
          >
            Trace
          </Button>
        </Box>
      </Box>
    </Paper>
  )
}

export default MomentCard
